"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDownIcon, XIcon } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/shared/components/dialog";
// Не общий @/shared/components/collapsible: тот несёт вёрстку FAQ на лендинге
// (свой шеврон, кегль 24px, левый бордер) и игнорирует переданный className.
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";
import { Button } from "@/shared/components/button";
import { Switch } from "@/shared/components/switch";
import { cn } from "@/shared/utils";
import { tp } from "@/shared/utils/typograf";
import type { CookieConsent } from "@/shared/utils/cookieConsent";

import { COOKIE_CATEGORIES, type CookieCategoryDescriptor } from "./categories";

const iconButton =
  "cursor-pointer rounded-md p-1 text-[#EEEEEE] transition-colors outline-none hover:text-white focus-visible:ring-2 focus-visible:ring-white/40";

// Крестик без паддингов, но с невидимой зоной клика вокруг — иначе цель 20×20.
const closeButton =
  "relative cursor-pointer rounded-sm text-[#EEEEEE] transition-colors outline-none after:absolute after:-inset-2 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40";

type CookieCategoryItemProps = {
  category: CookieCategoryDescriptor;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const CookieCategoryItem = (props: CookieCategoryItemProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const t = useTranslations("cookieConsent");

  const Icon = props.category.icon;
  const title = t(`categories.${props.category.key}.title`);

  return (
    <li>
      <CollapsiblePrimitive.Root
        data-slot="collapsible"
        open={isExpanded}
        onOpenChange={setIsExpanded}
      >
        <div className="flex items-center gap-3">
          <CollapsiblePrimitive.Trigger
            data-slot="collapsible-trigger"
            className="flex flex-1 cursor-pointer items-center gap-3 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#2A2A2A]">
              <Icon className="size-6 text-white" />
            </span>
            <span className="text-base leading-6 font-medium text-[#EEEEEE]">
              {tp.execute(title)}
            </span>
          </CollapsiblePrimitive.Trigger>
          <Switch
            checked={props.checked}
            disabled={props.category.required}
            aria-label={title}
            onCheckedChange={props.onCheckedChange}
          />

          <CollapsiblePrimitive.Trigger
            data-slot="collapsible-trigger"
            aria-hidden
            tabIndex={-1}
            className={iconButton}
          >
            <ChevronDownIcon
              className={cn(
                "size-4 transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </CollapsiblePrimitive.Trigger>
        </div>

        <CollapsiblePrimitive.Panel
          data-slot="collapsible-content"
          className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-200 ease-out data-ending-style:h-0 data-starting-style:h-0"
        >
          <p className="pt-2 text-sm leading-5 text-[#7B7B87]">
            {tp.execute(t(`categories.${props.category.key}.description`))}
          </p>
        </CollapsiblePrimitive.Panel>
      </CollapsiblePrimitive.Root>
    </li>
  );
};

export type CookieSettingsDialogProps = {
  open: boolean;
  consent: CookieConsent;
  onOpenChange: (open: boolean) => void;
  onCategoryChange: (
    category: CookieCategoryDescriptor["key"],
    checked: boolean
  ) => void;
  onSave: () => void;
  onRejectOptional: () => void;
};

export const CookieSettingsDialog = (props: CookieSettingsDialogProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("cookieConsent.settings");

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        ref={popupRef}
        initialFocus={popupRef}
        showCloseButton={false}
        overlayClassName="bg-[#121212CC] supports-backdrop-filter:backdrop-blur-[4px]"
        className="max-h-[calc(100vh-2rem)] max-w-[588px] gap-4 overflow-y-auto rounded-2xl bg-[#141414] p-4 text-white ring-0 sm:max-w-[588px]"
      >
        <DialogHeader className="flex-row items-center justify-between gap-4">
          <DialogTitle className="text-base leading-6 font-medium text-[#EEEEEE]">
            {tp.execute(t("title"))}
          </DialogTitle>
          <DialogClose className={closeButton}>
            <XIcon className="size-5" strokeWidth={1.5} />
            <span className="sr-only">{tp.execute(t("close"))}</span>
          </DialogClose>
        </DialogHeader>

        <ul className="space-y-4">
          {COOKIE_CATEGORIES.map((category) => (
            <CookieCategoryItem
              key={category.key}
              category={category}
              checked={props.consent[category.key]}
              onCheckedChange={(checked) =>
                props.onCategoryChange(category.key, checked)
              }
            />
          ))}
        </ul>

        <div className="flex gap-2">
          <Button
            className="h-11 flex-1 rounded-2xl px-4 text-sm"
            onClick={props.onSave}
          >
            {tp.execute(t("save"))}
          </Button>
          <Button
            variant="secondary"
            className="h-11 flex-1 rounded-2xl border-transparent bg-[#2A2A2A] px-4 text-sm hover:bg-[#343434]"
            onClick={props.onRejectOptional}
          >
            {tp.execute(t("rejectOptional"))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
